import api from "../../../utils/api";

// ============================================================================
// CRM Pipelines & Categories — frontend service. Talks to the
// torre-de-control-api crmPipelines module.
//
//   • pipelines (authed): GET/POST/PUT/DELETE  /crm/pipelines
//   • categories(authed): GET/POST/PUT/DELETE  /crm/categories
//
// This module is ALSO the single source of truth for category colors: the
// pipeline board and the leads inbox both resolve a category's Tailwind classes
// through `categoryClasses`, so recoloring a category in Settings recolors every
// surface at once. Colors are stored as a fixed token (not raw classes) so the
// classes can be precompiled here and survive Tailwind's purge.
// ============================================================================

export type CategoryTerminal = "open" | "won" | "lost";

export interface PipelineCategory {
  id: string;
  name: string;
  color: string; // a token from CATEGORY_COLOR_TOKENS
  order: number;
  terminal: CategoryTerminal;
  isSystem: boolean;
  /** Set on the six system categories — the lead status they back. */
  statusKey: string | null;
  archived?: boolean;
}

export interface Pipeline {
  id: string;
  name: string;
  description: string | null;
  /** Product this board tracks (Coding Cloud / Wayak / Nuvex); null = all. */
  product: string | null;
  isDefault: boolean;
  categoryIds: string[];
  /** Ordered, resolved category docs (server-side join). */
  categories: PipelineCategory[];
  archived?: boolean;
}

// --- Colors -----------------------------------------------------------------
// token → { dot (solid bg), accent (text), tone (badge bg+text), swatch }.
// Tones mirror the original LeadsLive STATUS_META so the migration is seamless.

export interface CategoryClasses {
  dot: string;
  accent: string;
  tone: string;
  swatch: string;
}

export const CATEGORY_COLORS: Record<string, CategoryClasses> = {
  sky: { dot: "bg-sky-500", accent: "text-sky-500", tone: "bg-sky-500/15 text-sky-500", swatch: "bg-sky-500" },
  amber: { dot: "bg-amber-500", accent: "text-amber-600 dark:text-amber-400", tone: "bg-amber-500/15 text-amber-600 dark:text-amber-400", swatch: "bg-amber-500" },
  primary: { dot: "bg-primary-500", accent: "text-primary-500", tone: "bg-primary-500/15 text-primary-500", swatch: "bg-primary-500" },
  gray: { dot: "bg-gray-400", accent: "text-gray-500", tone: "bg-gray-400/15 text-gray-500", swatch: "bg-gray-400" },
  emerald: { dot: "bg-emerald-500", accent: "text-emerald-500", tone: "bg-emerald-500/15 text-emerald-500", swatch: "bg-emerald-500" },
  red: { dot: "bg-red-500", accent: "text-red-500", tone: "bg-red-500/15 text-red-500", swatch: "bg-red-500" },
  indigo: { dot: "bg-indigo-500", accent: "text-indigo-500", tone: "bg-indigo-500/15 text-indigo-500", swatch: "bg-indigo-500" },
  cyan: { dot: "bg-cyan-500", accent: "text-cyan-500", tone: "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400", swatch: "bg-cyan-500" },
  violet: { dot: "bg-violet-500", accent: "text-violet-500", tone: "bg-violet-500/15 text-violet-500", swatch: "bg-violet-500" },
  orange: { dot: "bg-orange-500", accent: "text-orange-500", tone: "bg-orange-500/15 text-orange-600 dark:text-orange-400", swatch: "bg-orange-500" },
};

export const CATEGORY_COLOR_TOKENS = Object.keys(CATEGORY_COLORS);

const FALLBACK_CLASSES: CategoryClasses = CATEGORY_COLORS.gray;

/** Resolve a color token (or a whole category) to its Tailwind classes. */
export function categoryClasses(color: string | null | undefined): CategoryClasses {
  return (color && CATEGORY_COLORS[color]) || FALLBACK_CLASSES;
}

export const TERMINAL_LABELS: Record<CategoryTerminal, string> = {
  open: "Activa",
  won: "Ganado (cierre)",
  lost: "Perdido (cierre)",
};

// --- Pipelines --------------------------------------------------------------

export async function fetchPipelines(): Promise<Pipeline[]> {
  const res = await api.get("/crm/pipelines");
  return Array.isArray(res.data?.data) ? res.data.data : [];
}

export interface PipelineDraft {
  name: string;
  description?: string | null;
  product?: string | null;
  categoryIds?: string[];
}

export async function createPipeline(body: PipelineDraft): Promise<Pipeline> {
  const res = await api.post("/crm/pipelines", body);
  return res.data?.data;
}

export async function updatePipeline(
  id: string,
  patch: Partial<PipelineDraft> & { archived?: boolean },
): Promise<Pipeline> {
  const res = await api.put("/crm/pipelines", patch, { params: { id } });
  return res.data?.data;
}

export async function deletePipeline(id: string): Promise<void> {
  await api.delete("/crm/pipelines", { params: { id } });
}

// --- Templates --------------------------------------------------------------
// Starting points for new pipelines. A template's stages are previews; applying
// it ensures the (reusable) categories exist and returns their ordered ids.

export interface PipelineTemplateStage {
  name: string;
  color: string;
  terminal: CategoryTerminal;
  system: boolean;
}

export interface PipelineTemplate {
  key: string;
  name: string;
  description: string | null;
  stages: PipelineTemplateStage[];
}

export async function fetchPipelineTemplates(): Promise<PipelineTemplate[]> {
  const res = await api.get("/crm/pipeline-templates");
  return Array.isArray(res.data?.data) ? res.data.data : [];
}

/** Ensure a template's categories exist and return its ordered category ids. */
export async function applyPipelineTemplate(templateKey: string): Promise<string[]> {
  const res = await api.post("/crm/pipeline-templates/apply", { templateKey });
  return Array.isArray(res.data?.data?.categoryIds) ? res.data.data.categoryIds : [];
}

// --- Categories -------------------------------------------------------------

export async function fetchCategories(): Promise<PipelineCategory[]> {
  const res = await api.get("/crm/categories");
  return Array.isArray(res.data?.data) ? res.data.data : [];
}

export interface CategoryDraft {
  name: string;
  color?: string;
  terminal?: CategoryTerminal;
  order?: number;
}

export async function createCategory(body: CategoryDraft): Promise<PipelineCategory> {
  const res = await api.post("/crm/categories", body);
  return res.data?.data;
}

export async function updateCategory(
  id: string,
  patch: Partial<CategoryDraft> & { archived?: boolean },
): Promise<PipelineCategory> {
  const res = await api.put("/crm/categories", patch, { params: { id } });
  return res.data?.data;
}

export async function deleteCategory(id: string): Promise<void> {
  await api.delete("/crm/categories", { params: { id } });
}
